import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    RowSelectionState,
    SortingState,
    useReactTable
} from "@tanstack/react-table";
import classNames from "classnames";
import { ChangeEvent, useEffect, useState } from "react";
import { getPaginationDropdownOptions, getPaginationPageSizeOptions } from "../helpers/paginationHelper";
import { LBtn } from "./LBtn";
import { LSelect } from "./LSelect";

export const LTableResults = <T,>(
    data: T[],
    columns: Array<ColumnDef<T, any>>,
    options?: TableOptions,
    onRowSelectionChange?: (data: T[]) => void
): JSX.Element => {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const reactTable = useReactTable({
        data,
        columns,
        state: {
            rowSelection,
            sorting
        },
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        debugTable: true
    });

    const selectedRowsData = reactTable.getSelectedRowModel().flatRows.map((row) => row.original);
    useEffect(() => {
        onRowSelectionChange?.(selectedRowsData);
    }, [rowSelection]);

    const handlePageSize = (e: ChangeEvent<HTMLInputElement>): void => {
        reactTable.setPageSize(Number(e.target.value));
    };

    useEffect(() => {
        reactTable.setPageSize(options?.pageSize ?? 20);
    }, []);

    const tableClasses = classNames(
        "table",
        "is-fullwidth",
        options?.disableHover ? "" : "is-hoverable",
        options?.showStriping ? "is-striped" : "",
        options?.hideBorders ? "is-borderless" : "",
        options?.isSmall ? "is-narrow" : "",
        options?.isRoundedBody ? "is-rounded" : "",
        options?.isRoundedHeader ? "is-rounded-header" : ""
    );

    return (
        <>
            <table className={tableClasses}>
                <thead>
                    {reactTable.getHeaderGroups().map((hg) => (
                        <tr key={hg.id}>
                            {hg.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className={`${header.column.columnDef.meta?.headerClassName ?? ""} no-wrap`}
                                >
                                    <div
                                        {...{
                                            className: header.column.getCanSort() ? "cursor-pointer" : "",
                                            onClick: header.column.getToggleSortingHandler()
                                        }}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                        {header.column.getCanSort() && header.column.getIsSorted() && (
                                            <span className="icon is-small">
                                                {header.column.getIsSorted() === "asc" && (
                                                    <FontAwesomeIcon icon={faCaretDown} size={"sm"} />
                                                )}
                                                {header.column.getIsSorted() === "desc" && (
                                                    <FontAwesomeIcon icon={faCaretUp} size={"sm"} />
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {reactTable.getRowModel().rows.map((row) => (
                        <tr
                            key={row.id}
                            className={`${row.getIsSelected() ? "is-selected" : ""} ${
                                options?.hasSideBorders ? "has-left-and-right-borders" : ""
                            }`}
                        >
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {!options?.hidePaging && (
                <div>
                    <div className="mr-5 is-inline-block">
                        <LSelect
                            inputName="pagination-page-select"
                            label=""
                            value={(reactTable.getState().pagination.pageIndex + 1).toString()}
                            options={getPaginationDropdownOptions(reactTable.getPageCount())}
                            isInline={true}
                            classes="move-up-half"
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                reactTable.setPageIndex(Number(e.target.value) - 1)
                            }
                            theme="grey"
                        />
                    </div>
                    <div className="mr-5 mt-2 is-inline-block is-vtop">of {reactTable.getPageCount()}</div>
                    <LBtn
                        smallSpacing={true}
                        notRounded={true}
                        variation="outline"
                        onClick={() => reactTable.setPageIndex(0)}
                        disabled={!reactTable.getCanPreviousPage()}
                        theme="grey"
                    >
                        {"<<"}
                    </LBtn>
                    <LBtn
                        smallSpacing={true}
                        notRounded={true}
                        variation="outline"
                        onClick={() => reactTable.previousPage()}
                        disabled={!reactTable.getCanPreviousPage()}
                        theme="grey"
                    >
                        {"<"}
                    </LBtn>
                    <LBtn
                        smallSpacing={true}
                        notRounded={true}
                        variation="outline"
                        onClick={() => reactTable.nextPage()}
                        disabled={!reactTable.getCanNextPage()}
                        theme="grey"
                    >
                        {">"}
                    </LBtn>
                    <LBtn
                        smallSpacing={true}
                        notRounded={true}
                        variation="outline"
                        onClick={() => reactTable.setPageIndex(reactTable.getPageCount() - 1)}
                        disabled={!reactTable.getCanNextPage()}
                        theme="grey"
                    >
                        {">>"}
                    </LBtn>
                    <div className="is-pulled-right">
                        <div className="is-inline-block is-vtop mt-3 mr-3">Showing</div>
                        <div className="is-inline-block is-vtop mr-3">
                            <LSelect
                                inputName="pagination-page-size"
                                label=""
                                value={reactTable.getState().pagination.pageSize.toString()}
                                options={getPaginationPageSizeOptions()}
                                onChange={handlePageSize}
                            />
                        </div>
                        <div className="is-inline-block is-vtop mt-3">per page</div>
                    </div>
                </div>
            )}
        </>
    );
};

export interface TableOptions {
    hidePaging?: boolean;
    showStriping?: boolean;
    hideBorders?: boolean;
    pageSize?: number;
    isSmall?: boolean;
    disableSorting?: boolean;
    hasSideBorders?: boolean;
    isRoundedBody?: boolean;
    disableHover?: boolean;
    isRoundedHeader?: boolean;
}
